import React, { useState } from "react";
import ConfirmationModal from "../components/ConfirmationModal";
import { cancelAll } from "../api";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Spinner from "../components/Spinner";
import PaymentTimer from "../components/PaymentTimer";
import PageContainer, { GapSize } from "../components/PageContainer";
import OrderWrapper from "../components/OrderWrapper";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";

const OrderCancelPage: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session } = useCustomerAuth();

  const searchOrderId: string | null = searchParams.get("id");
  const [orderId, setOrderId] = useState<string>(
    location?.state?.orderId ?? searchOrderId,
  );
  // Buyer email — required to authorize the order lookup inside cancelAll
  // (see get-order.js). Seeded from navigation state / query param when present.
  const [email, setEmail] = useState<string>(
    location?.state?.email ?? searchParams.get("email") ?? "",
  );
  const [cancellationReason, setCancellationReason] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isFinalConfirmationOpen, setIsFinalConfirmationOpen] =
    useState<boolean>(false);
  const [isRedirectTimerOpen, setIsRedirectTimerOpen] =
    useState<boolean>(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const handleCancelOrder = async () => {
    setIsProcessing(true);
    setCancelError(null);
    try {
      await cancelAll(orderId, cancellationReason, session?.authToken, email);
      setIsFinalConfirmationOpen(false);
      setIsRedirectTimerOpen(true);
    } catch (error: any) {
      // cancelAll throws descriptive errors — surface them to the user
      setCancelError(
        error?.message ?? "Something went wrong. Please try again.",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRedirect = () => {
    setIsRedirectTimerOpen(false);
    navigate("/home");
  };

  return (
    <>
      {isProcessing && <Spinner text="Cancelling Order..." />}

      <ConfirmationModal
        isOpen={isRedirectTimerOpen}
        onConfirm={handleRedirect}
      >
        <div className="flex flex-col items-center p-8 gap-4">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ background: "#d1fae5", border: "3px solid #34d399" }}
          >
            <svg
              className="w-8 h-8"
              style={{ color: "#059669" }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2
            className="text-xl font-semibold playfair-text text-center"
            style={{ color: "#1a252f" }}
          >
            Order Refunded &amp; Canceled
          </h2>
          <p
            className="lora-text text-sm text-center"
            style={{ color: "#6b7280" }}
          >
            We hope we can serve you better next time. Redirecting you shortly…
          </p>
          <PaymentTimer seconds={4} onComplete={handleRedirect} />
        </div>
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={isFinalConfirmationOpen}
        onConfirm={handleCancelOrder}
        onCancel={() => setIsFinalConfirmationOpen(false)}
      >
        Are you sure you want to cancel your order?
      </ConfirmationModal>

      <PageContainer hasPadding gapSize={GapSize.Tiny}>
        <OrderWrapper>
          {/* Header */}
          <div className="order-page-header">
            <h1 className="order-page-title">Cancel Your Order</h1>
            <p className="order-page-subtitle">
              We're sorry to hear this order didn't work out.
            </p>
          </div>

          {/* Warning Banner */}
          <div className="order-cancel-warning">
            <svg
              className="w-5 h-5 flex-shrink-0 mt-0.5"
              style={{ color: "#d97706" }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
            <p className="order-cancel-warning-text">
              Orders can be canceled and fully refunded as long as{" "}
              <strong>production has not yet begun</strong>. Once your item
              enters production at our print partner, cancellation is no longer
              possible. If you need assistance, please{" "}
              <a
                href="/contact"
                className="underline"
                style={{ color: "#8b4513" }}
              >
                contact us
              </a>
              .
            </p>
          </div>

          {/* Message */}
          <p
            className="lora-text text-sm leading-relaxed"
            style={{ color: "#6b7280" }}
          >
            We are a small business dedicated to your satisfaction. If something
            wasn't right with your order, please let us know — we will do
            everything we can to make it right!
          </p>
          <div className="lora-text text-sm" style={{ color: "#6b7280" }}>
            <p>Thank you,</p>
            <p className="font-semibold" style={{ color: "#2c3e50" }}>
              The Americana &amp; Company Staff
            </p>
          </div>

          <div className="order-divider" />

          {/* Form */}
          <div className="flex flex-col gap-4">
            <div className="order-input-group">
              <label htmlFor="cancelOrderId" className="order-input-label">
                Order ID <span style={{ color: "#b5341f" }}>*</span>
              </label>
              <input
                id="cancelOrderId"
                type="text"
                name="orderId"
                placeholder="e.g. YzSh5Q3n5RnVpQ7JXTzK0mH8VZfZY"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                className="order-input-field max-w-lg"
                required
              />
            </div>
            <div className="order-input-group">
              <label htmlFor="cancelEmail" className="order-input-label">
                Email used on the order{" "}
                <span style={{ color: "#b5341f" }}>*</span>
              </label>
              <input
                id="cancelEmail"
                type="email"
                name="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="order-input-field max-w-lg"
                required
              />
            </div>
            <div className="order-input-group">
              <label htmlFor="cancelReason" className="order-input-label">
                Reason{" "}
                <span
                  className="opensans-text font-normal text-xs"
                  style={{ color: "#9ca3af", textTransform: "none" }}
                >
                  (optional)
                </span>
              </label>
              <input
                id="cancelReason"
                type="text"
                name="cancellationReason"
                placeholder="e.g. Ordered the wrong size"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                className="order-input-field max-w-lg"
              />
            </div>
          </div>

          <button
            type="button"
            className="order-action-btn order-action-btn--danger w-full max-w-xs"
            onClick={() => setIsFinalConfirmationOpen(true)}
          >
            Cancel Order
          </button>

          {/* Error */}
          {!!cancelError && (
            <div
              className="flex items-start gap-2 p-3 rounded-lg lora-text text-sm"
              style={{
                backgroundColor: "#fee2e2",
                color: "#991b1b",
                border: "1px solid #fca5a5",
              }}
            >
              <svg
                className="w-4 h-4 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              {cancelError}
            </div>
          )}
        </OrderWrapper>
      </PageContainer>
    </>
  );
};

export default OrderCancelPage;
